import { TranslocoService } from "@ngneat/transloco";
import {ValidationErrors} from "@angular/forms";
import {localizedDateString} from "./date-helper";

export function getErrorMessage(errors: ValidationErrors | null, transloco: TranslocoService, key?: string, displayAll: boolean = false): string[] {
    if (!errors) {
        return [];
    }

    if (displayAll) {
        return Object.keys(errors).map((errorKey: string) => getTranslatedMessage(errorKey, transloco, key, errors[errorKey]));
    } else {
        const firstErrorKey = Object.keys(errors)[0];
        return firstErrorKey ? [getTranslatedMessage(firstErrorKey, transloco, key, errors[firstErrorKey])] : [];
    }
}

function getTranslatedMessage(errorType: string, transloco: TranslocoService, key?: string, params?: any,): string {
    if (!transloco) {
        return "";
    }

    switch (errorType) {
        case 'couponInvalid':
            return transloco.translate('app.register.to.race.apply.coupon.error.does.not.exist');

        case 'emailUniqueInvalid':
            return transloco.translate('app.register.to.race.email.is.not.unique');

        case 'required':
            return transloco.translate('app.common.error.required');

        case 'maxlength':
            if (key === 'picture') {
                return transloco.translate('app.common.error.medium.text.validator');
            }
            if (params?.['requiredLength']) {
                return transloco.translate('app.common.error.maxlength', { maxLength: params['requiredLength'] });
            }
            return transloco.translate('app.common.error');

        case 'minlength':
            if (params?.['requiredLength'] !== undefined) {
                return transloco.translate('app.common.error.min.length', { value: params['requiredLength'] });
            }
            return transloco.translate('app.common.error');

        case 'max':
        case 'min':
            const val = (params?.[errorType] !== undefined) ? params[errorType] : params;
            if (val !== undefined) {
                return transloco.translate(`app.common.error.${errorType}.value`, { value: val });
            }
            return transloco.translate("app.common.error.default");

        case 'email':
            return transloco.translate('app.common.error.email');

        case 'alphabetical':
            return transloco.translate('app.common.error.alphabetical');

        case 'numerical':
            return transloco.translate('app.common.error.numerical');

        case 'pattern':
            return transloco.translate('app.common.error.pattern');

        case 'duplicate':
            return transloco.translate('app.common.error.duplicate');

        case 'decimal':
            return transloco.translate('app.common.error.decimal');

        case 'invalidPhoneNumber':
            return transloco.translate('app.common.error.phone');

        case 'invalidDate':
            return transloco.translate('app.common.error.invalidDate');

        case 'length':
            if (isPasswordField(key)) {
                return transloco.translate('app.common.error.password.length', {
                    min: params?.['min'],
                    max: params?.['max']
                });
            }
            return transloco.translate('app.common.error');

        case 'uppercase':
            if (isPasswordField(key)) {
                return transloco.translate('app.common.error.password.uppercase');
            }
            return transloco.translate('app.common.error');

        case 'lowercase':
            if (isPasswordField(key)) {
                return transloco.translate('app.common.error.password.lowercase');
            }
            return transloco.translate('app.common.error');

        case 'digit':
            if (isPasswordField(key)) {
                return transloco.translate('app.common.error.password.digit');
            }
            return transloco.translate('app.common.error');

        case 'special':
            if (isPasswordField(key)) {
                return transloco.translate('app.common.error.password.special', {
                    special: params?.['chars']?.split("").join("  ")
                });
            }
            return transloco.translate('app.common.error');

        case 'whitespace':
            if (isPasswordField(key)) {
                return transloco.translate('app.common.error.password.whitespace');
            }
            return transloco.translate('app.common.error');

        case 'date':
            if (key === "time") {
                return transloco.translate('app.common.error.time.min', {
                    date: localizedDateString(params?.['min'], 'dd.MM.yyyy HH:mm:ss.SSS', transloco.getActiveLang())
                });
            }
            if (key === 'from') {
                return transloco.translate('app.common.error.start.date');
            }
            if (key === "to") {
                return transloco.translate('app.common.error.end.date');
            }
            return transloco.translate('app.common.error.default');

        case 'paymentDataMissing':
            if (params === 'iban') {
                return transloco.translate('app.common.error.payment.data.missing.iban');
            }
            return transloco.translate('app.common.error.payment.data.missing');

        case 'mandatoryPaymentMissing':
            return transloco.translate("app.common.error.mandatory.payment.missing", {
                field: transloco.translate("event.payment.settings.mandatory.payment")
            });

        case 'groupRegistrationPaymentEnabled':
            return transloco.translate("app.common.error.group.registration.payment.enabled", { races: params });

        case 'chipNumbers':
            return transloco.translate("race.management.competitors.assign.chip.numbers.input.format.error");

        case 'dateOutOfRange':
            return getDateOutOfRangeError(params, key, transloco);
        case 'defaultMinDate':
            return transloco.translate('app.common.error.defaultMinDate', {
                date: localizedDateString(params?.['min'], 'shortDate', transloco.getActiveLang())
            })
        case 'defaultMaxDate':
            return transloco.translate('app.common.error.defaultMaxDate', {
                date: localizedDateString(params?.['max'], 'shortDate', transloco.getActiveLang())
            })
        case 'minDate':
        case 'maxDate':
            return getDateBoundaryError(key, params, transloco);
        default:
            return transloco.translate('app.common.error.default');
    }
}

function isPasswordField(key?: string): boolean {
    return key === 'password' || key === 'confirmPassword';
}

function getDateOutOfRangeError(params: any, key: string | undefined, transloco: TranslocoService): string {
    const startDate = localizedDateString(params?.ranges?.from, 'shortDate', transloco.getActiveLang());
    const startTime = localizedDateString(params?.ranges?.from, 'shortTime', transloco.getActiveLang());
    const endDate = localizedDateString(params?.ranges?.to, 'shortDate', transloco.getActiveLang());
    const endTime = localizedDateString(params?.ranges?.to, 'shortTime', transloco.getActiveLang());

    let translationKey = "";
    if (key === 'raceStart' || key === 'raceEnd') {
        translationKey = "race.details.start.end.date.range.error";
    } else if (key === 'registrationStart' || key === 'registrationEnd') {
        translationKey = "race.details.start.end.application.range.error";
    }

    if (params?.['message']) {
        translationKey = params['message'];
    }

    return transloco.translate(translationKey, {
        startDate,
        startTime,
        endDate,
        endTime
    });
}

function getDateBoundaryError(key: string | undefined, params: any, transloco: TranslocoService): string {
    if (key === "time") {
        let translocoKey : string = 'app.common.error.time.invalid';
        let date: Date | undefined;
        if (params?.['min']) {
            translocoKey = 'app.common.error.time.min';
            date = params?.['min'];
        }
        else if (params?.['max']) {
            translocoKey = 'app.common.error.time.max';
            date = params?.['max'];
        }
        return transloco.translate(translocoKey, {
            date: date ? localizedDateString(date, 'dd.MM.yyyy HH:mm:ss.SSS', transloco.getActiveLang()) : ''
        });
    }

    const startDateKeys = ['from', 'raceStart', 'registrationStart', 'start'];
    const endDateKeys = ['to', 'raceEnd', 'registrationEnd', 'end'];

    if (startDateKeys.includes(key || '')) {
        return transloco.translate('app.common.error.start.date');
    }
    if (endDateKeys.includes(key || '')) {
        return transloco.translate('app.common.error.end.date');
    }

    return transloco.translate('app.common.error.default');
}